import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Page } from '../Layout/PageLayout';

const Layout = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-height: calc(100vh - var(--header-height));
`;

const Sidebar = styled.aside`
  flex: 1 1 15.5rem;
  background: var(--surface-1);
  border-right: 1px solid var(--border-subtle);
  padding: 1rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (min-width: 900px) {
    position: sticky;
    top: var(--header-height);
    height: calc(100vh - var(--header-height));
    overflow-y: auto;
  }
`;

const Main = styled.main`
  flex: 999 1 40rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1.5rem;
  padding: 1rem 1.75rem;
  border-bottom: 1px solid var(--border-subtle);

  h1 {
    font-size: 1.625rem;
    letter-spacing: -0.02em;
  }
`;

const Crumb = styled.div`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-tertiary);
  margin-bottom: 2px;
`;

const Content = styled.div`
  padding: 1.5rem 1.75rem 2.5rem;
  flex-grow: 1;

  @media (max-width: 600px) {
    padding: 1.25rem 1rem 2rem;
  }
`;

const Label = styled.div`
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  color: var(--text-tertiary);
  padding: ${(p) => p.$pad || '1.125rem 0.625rem 0.375rem'};
`;

const Switcher = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: ${(p) => (p.$compact ? '0.5625rem 0.625rem' : '0.625rem')};
  background: ${(p) => (p.$compact ? 'var(--card)' : 'transparent')};
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  color: var(--text-primary);
  text-align: left;

  &:hover {
    border-color: var(--border-accent);
  }
`;

const Avatar = styled.span`
  width: 2rem;
  height: 2rem;
  border-radius: 0.4375rem;
  background: var(--brand);
  color: var(--brand-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  flex-shrink: 0;
`;

const Grow = styled.span`
  flex-grow: 1;
  min-width: 0;

  b,
  small {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  b {
    font-size: 0.875rem;
    font-weight: ${(p) => (p.$light ? 500 : 600)};
  }

  small {
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--text-tertiary);
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  background: var(--surface-3);
  border: 1px solid var(--border-accent);
  border-radius: 0.5rem;
  padding: 0.375rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  max-height: 20rem;
  overflow-y: auto;
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5625rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  text-align: left;
  color: ${(p) => (p.$accent ? 'var(--brand)' : 'var(--text-primary)')};
  background: ${(p) => (p.$active ? '#1f2329' : 'transparent')};

  &:hover {
    background: #1f2329;
  }

  small {
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--text-tertiary);
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  min-height: 2.375rem;
  padding: 0 0.625rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  text-align: left;
  background: ${(p) => (p.$active ? '#1a1d22' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  font-weight: ${(p) => (p.$active ? 600 : 400)};

  &:hover {
    color: var(--text-primary);
    background: #15181d;
  }

  svg {
    flex-shrink: 0;
    color: ${(p) => (p.$active ? 'var(--brand)' : 'var(--text-tertiary)')};
  }

  small {
    margin-left: auto;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--text-tertiary);
    font-weight: 400;
  }
`;

const Usage = styled.div`
  margin-top: auto;
  padding: 0.875rem 0.625rem 0.25rem;
  font-size: 0.75rem;
  color: var(--text-tertiary);

  div:first-child {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    color: var(--text-secondary);
  }

  b {
    font-family: var(--font-mono);
    font-weight: 400;
    color: var(--text-primary);
  }
`;

const Bar = styled.div`
  height: 4px;
  border-radius: 2px;
  background: var(--border);
  overflow: hidden;
  margin-bottom: 0.5rem;

  span {
    display: block;
    height: 100%;
    width: ${(p) => p.$pct}%;
    background: ${(p) => (p.$pct >= 90 ? 'var(--error)' : p.$pct >= 70 ? 'var(--warning)' : 'var(--brand)')};
  }
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--live);
  flex-shrink: 0;
`;

const Icon = ({ d }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d.map((path) => (
      <path key={path} d={path} />
    ))}
  </svg>
);

const ICONS = {
  sessions: ['M4 6h16', 'M4 12h16', 'M4 18h10'],
  funnels: ['M3 5h18l-7 8v6l-4-2v-4z'],
  heatmaps: ['M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z', 'M12 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z'],
  settings: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z'],
  workspace: ['M3 7l9-4 9 4-9 4-9-4z', 'M3 12l9 4 9-4', 'M3 17l9 4 9-4'],
  platform: ['M12 3l2.7 5.5 6 .9-4.3 4.2 1 6L12 16.8 6.6 19.6l1-6L3.3 9.4l6-.9z'],
};

// Closes when clicking anywhere outside `ref`.
function useDismiss(open, setOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, setOpen]);
  return ref;
}

const AnalyticsShell = ({
  workspaces,
  selectedWorkspace,
  onSelectWorkspace,
  projects,
  selectedProject,
  onSelectProject,
  onNewProject,
  section,
  onNavigate,
  canManageWorkspaces,
  isPlatformAdmin,
  sessionUsage,
  retentionDays,
  crumb,
  title,
  actions,
  children,
}) => {
  const [wsOpen, setWsOpen] = useState(false);
  const [projOpen, setProjOpen] = useState(false);
  const wsRef = useDismiss(wsOpen, setWsOpen);
  const projRef = useDismiss(projOpen, setProjOpen);

  const nav = (id, label, meta) => (
    <NavItem
      key={id}
      type="button"
      $active={section === id}
      aria-current={section === id ? 'page' : undefined}
      onClick={() => onNavigate(id)}
    >
      <Icon d={ICONS[id]} />
      {label}
      {meta && <small>{meta}</small>}
    </NavItem>
  );

  const usagePct = sessionUsage ? Math.min(100, Math.round(sessionUsage.ratio * 100)) : 0;

  return (
    <Page>
      <Layout>
        <Sidebar aria-label="Analytics navigation">
          <div ref={wsRef} style={{ position: 'relative', marginBottom: '0.5rem' }}>
            <Switcher type="button" aria-expanded={wsOpen} aria-haspopup="menu" onClick={() => setWsOpen((o) => !o)}>
              <Avatar>{(selectedWorkspace?.name || '?').charAt(0).toUpperCase()}</Avatar>
              <Grow>
                <b>{selectedWorkspace?.name || 'No workspace'}</b>
                <small>{selectedWorkspace?.plan_name || selectedWorkspace?.plan_id || 'Starter'} plan</small>
              </Grow>
              <Icon d={['M7 10l5-5 5 5', 'M7 14l5 5 5-5']} />
            </Switcher>
            {wsOpen && (
              <Menu role="menu">
                <Label $pad="0.375rem 0.5rem">WORKSPACES</Label>
                {workspaces.map((w) => (
                  <MenuItem
                    key={w.id}
                    type="button"
                    role="menuitem"
                    $active={w.id === selectedWorkspace?.id}
                    onClick={() => {
                      onSelectWorkspace(w.id);
                      setWsOpen(false);
                    }}
                  >
                    <span>{w.name}</span>
                    <small>{w.plan_name || w.plan_id}</small>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </div>

          <Label>PROJECT</Label>
          <div ref={projRef} style={{ position: 'relative' }}>
            <Switcher
              type="button"
              $compact
              aria-expanded={projOpen}
              aria-haspopup="menu"
              onClick={() => setProjOpen((o) => !o)}
              disabled={projects.length === 0 && !canManageWorkspaces}
            >
              {selectedProject && <LiveDot />}
              <Grow $light>
                <b>{selectedProject?.name || (projects.length ? 'Choose a project' : 'No projects yet')}</b>
              </Grow>
              <Icon d={['M6 9l6 6 6-6']} />
            </Switcher>
            {projOpen && (
              <Menu role="menu">
                {projects.map((p) => (
                  <MenuItem
                    key={p.id}
                    type="button"
                    role="menuitem"
                    $active={p.id === selectedProject?.id}
                    onClick={() => {
                      onSelectProject(p.id);
                      setProjOpen(false);
                    }}
                  >
                    <span>{p.name}</span>
                  </MenuItem>
                ))}
                {canManageWorkspaces && (
                  <MenuItem
                    type="button"
                    role="menuitem"
                    $accent
                    onClick={() => {
                      onNewProject();
                      setProjOpen(false);
                    }}
                  >
                    + New project
                  </MenuItem>
                )}
              </Menu>
            )}
          </div>

          <Label>EXPLORE</Label>
          <nav aria-label="Explore" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nav('sessions', 'Sessions', sessionUsage ? sessionUsage.count.toLocaleString() : null)}
            {nav('funnels', 'Funnels')}
            {nav('heatmaps', 'Heatmaps')}
          </nav>

          <Label>MANAGE</Label>
          <nav aria-label="Manage" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nav('settings', 'Project settings')}
            {canManageWorkspaces && nav('workspace', 'Workspace')}
            {isPlatformAdmin && nav('platform', 'Platform admin')}
          </nav>

          {sessionUsage && (
            <Usage>
              <div>
                <span>Stored sessions</span>
                <b>
                  {sessionUsage.count.toLocaleString()} / {sessionUsage.limit.toLocaleString()}
                </b>
              </div>
              <Bar $pct={usagePct}>
                <span />
              </Bar>
              <div>Raw data kept {retentionDays} days</div>
            </Usage>
          )}
        </Sidebar>

        <Main>
          <TopBar>
            <div style={{ minWidth: 0 }}>
              <Crumb>{crumb}</Crumb>
              <h1>{title}</h1>
            </div>
            {actions && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>{actions}</div>}
          </TopBar>
          <Content>{children}</Content>
        </Main>
      </Layout>
    </Page>
  );
};

export default AnalyticsShell;
